import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, Tag, Tooltip, Button, Input, Select, Modal, Alert, Space, Typography } from 'antd';
import { SearchOutlined, EditOutlined, CloseOutlined } from '@ant-design/icons';
import DataCard from '../page/DataCard';
import { DASHBOARD_TABLE_STICKY } from '../../utils/adminDashboardTable';
import { errorText } from './taxonomyApi';
import './taxonomy.css';

const { Text } = Typography;

const LS_COLUMNS_KEY = 'policylens:taxonomy-matrix-columns:v1';
const PAGE_SIZE = 20;

function readSavedColumns() {
  try {
    const raw = localStorage.getItem(LS_COLUMNS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === 'string') : null;
  } catch {
    return null;
  }
}
function saveColumns(keys) {
  try { localStorage.setItem(LS_COLUMNS_KEY, JSON.stringify(keys)); } catch { /* storage unavailable: choice just is not remembered */ }
}

/** Columns to show: the saved choice limited to loaded frameworks, else every loaded framework that has mappings. */
export function resolveColumns(frameworks, saved) {
  const loaded = new Set(frameworks.filter((f) => f.loaded).map((f) => f.key));
  if (saved) return saved.filter((k) => loaded.has(k));
  return frameworks.filter((f) => f.loaded && f.mappedControlCount > 0).map((f) => f.key);
}

const SOURCE_LABEL = { seed: 'seed', legacy: 'legacy', admin: 'admin' };

function CellTags({ targets }) {
  if (!targets || !targets.length) return <Text type="secondary">-</Text>;
  return (
    <div className="pl-tax-cell">
      {targets.map((t) => (
        <Tooltip key={t.targetRef} title={`${t.targetName || t.targetRef}${t.source ? ` (source: ${t.source})` : ''}`}>
          <Tag data-source={t.source || undefined}>
            {t.targetRef}
            {t.source ? <span className="pl-tax-src">{SOURCE_LABEL[t.source] || t.source}</span> : null}
          </Tag>
        </Tooltip>
      ))}
    </div>
  );
}

/**
 * Mount only once `frameworks` has loaded (the default columns are resolved from it at mount).
 * Cross-Framework Mapping tab: ISO 27002 controls down the side, a user-chosen set of regulations across.
 * Reads GET /control-taxonomy/mapping-matrix; a cell edit PUTs the full target set for that (ISO, framework) pair.
 * `canEdit` only hides the affordance; the API is the enforcement point (platform_admin).
 */
const MappingMatrix = ({ api, frameworks, canEdit, onChanged }) => {
  const [columns, setColumns] = useState(() => resolveColumns(frameworks, readSavedColumns()));
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ status: 'loading', rows: [], total: 0, message: null });
  const [editing, setEditing] = useState(null); // { row, fwKey, selected, saving, error }
  const [targets, setTargets] = useState({}); // fwKey -> { status, options }
  const loadSeq = useRef(0);

  const columnsKey = columns.join(',');
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const { rows, total } = await api.matrix({ frameworks: columnsKey ? columnsKey.split(',') : [], search: query, page, pageSize: PAGE_SIZE });
      if (seq === loadSeq.current) setState({ status: 'ready', rows, total, message: null });
    } catch (err) {
      if (seq === loadSeq.current) setState({ status: 'error', rows: [], total: 0, message: errorText(err, 'Could not load the mapping matrix.') });
    }
  }, [api, columnsKey, query, page]);

  useEffect(() => { load(); }, [load]);

  const fwByKey = useMemo(() => Object.fromEntries(frameworks.map((f) => [f.key, f])), [frameworks]);

  const changeColumns = (next) => { setColumns(next); saveColumns(next); setPage(1); };

  const ensureTargets = useCallback(async (fwKey) => {
    if (targets[fwKey]?.status === 'ready' || targets[fwKey]?.status === 'loading') return;
    setTargets((t) => ({ ...t, [fwKey]: { status: 'loading', options: [] } }));
    try {
      const options = await api.frameworkControls(fwKey);
      setTargets((t) => ({ ...t, [fwKey]: { status: 'ready', options } }));
    } catch (err) {
      setTargets((t) => ({ ...t, [fwKey]: { status: 'error', options: [], message: errorText(err, 'Could not load this regulation\'s controls.') } }));
    }
  }, [api, targets]);

  const openEditor = (row, fwKey) => {
    setEditing({ row, fwKey, selected: (row.cells?.[fwKey] || []).map((t) => t.targetRef), saving: false, error: null });
    ensureTargets(fwKey);
  };

  const patchRowCell = (isoControl, fwKey, cell) =>
    setState((s) => ({ ...s, rows: s.rows.map((r) => (r.isoControl === isoControl ? { ...r, cells: { ...r.cells, [fwKey]: cell } } : r)) }));

  const save = async () => {
    const { row, fwKey, selected } = editing;
    const previous = row.cells?.[fwKey] || [];
    const nameOf = Object.fromEntries((targets[fwKey]?.options || []).map((o) => [o.ref, o.name]));
    // Optimistic: show the new set straight away, roll back if the API refuses it.
    patchRowCell(row.isoControl, fwKey, selected.map((ref) => ({ targetRef: ref, targetName: nameOf[ref] || previous.find((p) => p.targetRef === ref)?.targetName || '', source: 'admin' })));
    setEditing((e) => ({ ...e, saving: true, error: null }));
    try {
      const saved = await api.saveCell(row.isoControl, fwKey, selected);
      const cell = Array.isArray(saved) ? saved : Array.isArray(saved?.cell) ? saved.cell : Array.isArray(saved?.targets) ? saved.targets : null;
      if (cell) patchRowCell(row.isoControl, fwKey, cell);
      setEditing(null);
      if (onChanged) onChanged();
    } catch (err) {
      patchRowCell(row.isoControl, fwKey, previous);
      setEditing((e) => ({ ...e, saving: false, error: errorText(err, 'Could not save this mapping.') }));
    }
  };

  const cols = (() => {
    const out = [
      {
        title: 'ISO 27002 control',
        key: 'iso',
        fixed: 'left',
        width: 260,
        render: (_, r) => (
          <div>
            <Text strong>{r.isoControl}</Text>
            <div className="pl-tax-isoname">{r.isoControlName}</div>
          </div>
        ),
      },
    ];
    columns.forEach((k) => {
      const f = fwByKey[k];
      if (!f) return;
      out.push({
        key: k,
        width: 220,
        title: (
          <div className="pl-tax-head">
            <div className="pl-tax-head__top">
              <Text strong>{f.label}</Text>
              <Button type="text" size="small" aria-label={`Remove ${f.label} column`} icon={<CloseOutlined />} onClick={() => changeColumns(columns.filter((c) => c !== k))} />
            </div>
            <Text type="secondary" className="pl-tax-head__sub">mapped {f.mappedControlCount} of {f.controlCount} controls</Text>
          </div>
        ),
        render: (_, r) => {
          const cell = r.cells?.[k];
          return (
            <div className="pl-tax-cellwrap">
              <CellTags targets={cell} />
              {canEdit && (
                <Button type="text" size="small" aria-label={`Edit ${f.label} mapping for ${r.isoControl}`} icon={<EditOutlined />} onClick={() => openEditor(r, k)} />
              )}
            </div>
          );
        },
      });
    });
    return out;
  })();

  const scrollX = 260 + columns.length * 220;
  const addOptions = frameworks
    .filter((f) => !columns.includes(f.key))
    .map((f) => ({
      value: f.key,
      disabled: !f.loaded,
      label: f.loaded ? f.label : `${f.label} - Load controls in Policy Hub first`,
    }));
  const editFw = editing ? fwByKey[editing.fwKey] : null;
  const editTargets = editing ? targets[editing.fwKey] : null;

  return (
    <>
      <DataCard size="small">
        <Space wrap size="middle">
          <Input
            placeholder="Search ISO controls or mapped references"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => { setSearch(e.target.value); if (!e.target.value) { setQuery(''); setPage(1); } }}
            onPressEnter={() => { setQuery(search.trim()); setPage(1); }}
            allowClear
            style={{ width: 320 }}
            data-testid="matrix-search"
          />
          <Button onClick={() => { setQuery(search.trim()); setPage(1); }}>Search</Button>
          <Select
            placeholder="Add regulation"
            value={null}
            style={{ width: 300 }}
            options={addOptions}
            onChange={(k) => k && changeColumns([...columns, k])}
            data-testid="add-regulation"
          />
          <Text type="secondary">{state.total} ISO controls</Text>
        </Space>
      </DataCard>

      {state.status === 'error' && (
        <Alert type="error" showIcon data-testid="matrix-error" message={state.message} action={<Button size="small" onClick={load}>Retry</Button>} />
      )}

      <Table
        className="policylens-data-table pl-tax-table"
        columns={cols}
        dataSource={state.rows}
        rowKey={(r) => r.isoControl}
        size="small"
        loading={state.status === 'loading'}
        sticky={DASHBOARD_TABLE_STICKY}
        scroll={{ x: scrollX }}
        bordered
        locale={{ emptyText: query ? 'No ISO controls match this search.' : 'No ISO controls to show.' }}
        pagination={{ current: page, pageSize: PAGE_SIZE, total: state.total, showSizeChanger: false, onChange: setPage, showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} ISO controls` }}
      />

      <Modal
        open={!!editing}
        title={editing ? `${editing.row.isoControl} - ${editFw?.label || editing.fwKey}` : ''}
        okText="Save"
        cancelText="Cancel"
        confirmLoading={editing?.saving}
        onOk={save}
        onCancel={() => setEditing(null)}
        destroyOnClose
      >
        {editing && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">{editing.row.isoControlName}</Text>
            {editing.error && <Alert type="error" showIcon data-testid="cell-error" message={editing.error} />}
            {editTargets?.status === 'error' && <Alert type="error" showIcon message={editTargets.message} />}
            <Select
              mode="multiple"
              showSearch
              style={{ width: '100%' }}
              placeholder={`Search ${editFw?.label || 'regulation'} controls`}
              loading={editTargets?.status === 'loading'}
              value={editing.selected}
              onChange={(v) => setEditing((e) => ({ ...e, selected: v }))}
              optionFilterProp="label"
              options={(editTargets?.options || []).map((o) => ({ value: o.ref, label: `${o.ref} ${o.name}`.trim() }))}
              data-testid="cell-select"
            />
          </Space>
        )}
      </Modal>
    </>
  );
};

export default MappingMatrix;
